import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { Container, Card, Form, Row, Col, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFileExcel, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import 'react-widgets/styles.css';
import AppLoading from 'src/components/common/AppLoading';
import MaxDate from 'src/components/common/MaxDate';
import MultiselectComponent from 'src/components/common/MultiselectComponent';
import OrganismTreeSelect from '../Users/utils/OrganismTreeSelect';
import { isNotEmptyArray } from 'src/services/validationService';
import { ROUTE_PATHS as ROUTES } from 'src/routes';
import { getGlobalDataPeriodsData, getGlobalDataSelectedPeriod, getReportDatesData } from 'src/redux/globalData/globalDataReducer';
import { tryGetReportBalanceStatus } from 'src/redux/reports/reportsActionCreactor';
import { getReportBalanceStatusExecutionIsFetching } from 'src/redux/reports/reportsReducer';
import { getListAllServiceAdministrativeByUserData, getListAllServiceAdministrativeByUserIsFetching } from 'src/redux/administrativeService/administrativeServiceReducer';
import { tryListAllServiceAdministrativeByUser } from 'src/redux/administrativeService/administrativeServiceActionCreator';
import { getEmail, getProfileName } from 'src/redux/login/loginReducer';
import { getUserListData } from 'src/redux/user/userReducer';
import { tryGetUserList } from 'src/redux/user/userActionCreator';
import { clearListAllServiceAdministrativeByUser } from 'src/redux/administrativeService/administrativeServiceActions';
import { shortlistedService } from 'src/utils/utils';
import { useJurisdictions } from '../Users/utils/useJurisdictions';
import { config } from 'src/env.js';

const ReportBalanceStatusPage = () => {
    const dispatch = useDispatch();
    const hookFormMethods = useForm();

    // Hook para jurisdicciones
    const { 
        jurisdictionData, 
        isLoading: jurisdictionsLoading,
        fetchJurisdictions,
		processSelectedOrganisms,
		fetchUserOrganisms,
		userOrganisms
    } = useJurisdictions();

    const [selectedOrganisms, setSelectedOrganisms] = useState([]);
    const [filteredJurisdictionData, setFilteredJurisdictionData] = useState([]);
    const profileName = useSelector(state => getProfileName(state));
    const reportDates = useSelector(state => getReportDatesData(state));

    // User Id
    const email = useSelector(state => getEmail(state));
    const listUserData = useSelector(state => getUserListData(state));
    const userId = listUserData?.records?.find(item => item.email == email);

    // Periods
    const periodList = useSelector(state => getGlobalDataPeriodsData(state))?.data?.sort((a, b) => a.year < b.year ? 1 : -1);
    const hasPeriodList = isNotEmptyArray(periodList);
    const globalSelectedPeriod = useSelector(state => getGlobalDataSelectedPeriod(state));

    // Services Administrative
    const administrativeService = useSelector(state => getListAllServiceAdministrativeByUserData(state))?.sort((a, b) => a.code > b.code ? 1 : -1);
    const administrativeServiceIsFetching = useSelector(state => getListAllServiceAdministrativeByUserIsFetching(state));

    const [dateTo, setDateTo] = useState(reportDates?.dateTo);
    const [dateHasErrors, setDateHasErrors] = useState(false);
    const [showMsgReportInProcess, setShowMsgReportInProcess] = useState(false);
    const [checkWithoutBalance, setCheckWithoutBalance] = useState(true);
    const checkWithoutBalanceLabel = 'Mostrar solo los Expedientes con las etapas del gasto no igualadas';

    // Multiselect
    const [options, setOptions] = useState([]);
    const [selected, setSelected] = useState([]);
    
    // Filtrar jurisdictionData según los organismos del usuario
    useEffect(() => {
        if (profileName === 'Consulta por Organismo' && userOrganisms.length > 0 && jurisdictionData.length > 0) {
            const userOrganismIds = userOrganisms.map(org => `organism-${org.id}`);
            
            const filtered = jurisdictionData.map(jurisdiction => {
                if (jurisdiction.children) {
                    const filteredChildren = jurisdiction.children.filter(child => 
                        userOrganismIds.includes(child.value)
                    );
                    
                    if (filteredChildren.length > 0) {
                        return {
                            ...jurisdiction,
                            children: filteredChildren
                        };
                    }
                }
                return null;
            }).filter(item => item !== null);
            
            setFilteredJurisdictionData(filtered);
        } else {
            setFilteredJurisdictionData(jurisdictionData);
        }
    }, [jurisdictionData, userOrganisms, profileName]);

    useEffect(() => {
        dispatch(clearListAllServiceAdministrativeByUser());
        dispatch(tryGetUserList());
        dispatch(tryListAllServiceAdministrativeByUser(userId?.id));
    }, []);

    useEffect(() => {
        fetchJurisdictions();
        if (userId?.id && profileName === 'Consulta por Organismo') {
            fetchUserOrganisms(userId.id);
        }
    }, [fetchJurisdictions, userId?.id, profileName, fetchUserOrganisms]);

    useEffect(() => {
        let optionsArray = [];
        let i = 0;
        for (i in administrativeService) {
            optionsArray.push({
                label: administrativeService[i]?.code + " - " + administrativeService[i]?.shortName,
                value: administrativeService[i]?.id
            });
        }
        setOptions(optionsArray);
    }, [administrativeService]);

    useEffect(() => {
        const uniqueService = shortlistedService(options);
        if (uniqueService) {
            setSelected(options);
        }
    }, [options]);

    const getReport = outputFormat => {
        if (!globalSelectedPeriod) return;

        setShowMsgReportInProcess(false);
        const params = {
            outputFormat,
            exerciseId: globalSelectedPeriod?.id,
            dateTo,
            withoutBalance: checkWithoutBalance ? 1 : 0,
        };

        // Agregar propiedades según el perfil
        if (profileName === 'Consulta por Organismo') {
            const organismIds = processSelectedOrganisms(selectedOrganisms);
            if (organismIds && organismIds.length > 0) {
                params.organizationIds = organismIds;
            } else {
                console.warn('No se seleccionaron organismos');
                return;
            }
        } else {
            const serviceIds = selected?.map(service => service?.value);
            if (serviceIds && serviceIds.length > 0) {
                params.serviceIds = serviceIds;
            } else {
                console.warn('No se seleccionaron servicios');
                return;
            }
        }

        dispatch(tryGetReportBalanceStatus(params)).then(
            response => {
                if (response?.status == 200) {
                    setShowMsgReportInProcess(true);
                }
            }
        );
    };

    const onClickPDF = () => getReport(1);
    const onClickXLS = () => getReport(2);

    // Reports redux
    const reportIsFetching = useSelector(state => getReportBalanceStatusExecutionIsFetching(state));
    const isFormValid = () => {
        const hasBasicRequirements = hasPeriodList && !reportIsFetching && dateTo && !dateHasErrors;
        if (!hasBasicRequirements) return false;

        // Validación específica según perfil
        if (profileName === 'Consulta por Organismo') {
            return selectedOrganisms && selectedOrganisms.length > 0;
        }
        return selected && selected.length > 0;
    };

    return (
        <Container fluid>
            <Card className='mb-3'>
                <Card.Header className='d-flex justify-content-between'>
                    <h1 className="h6 mt-1 mb-0">Reporte de estado de saldos</h1>
                    <a
                        className='text-white'
                        target="_blank"
                        href={config.apis.baseDeConocimientos.URL}
                    >
                        <FontAwesomeIcon icon={faInfoCircle} className='mr-2' />
                        <small>Ver manual de uso</small>
                    </a>
                </Card.Header>
                <Card.Body>
                    <Form>
                        <Row className='justify-content-center'>
                            <Col sm={8}>
                                <Form.Group>
                                    <Form.Label className='text-black-color'>
                                        Ejercicio
                                    </Form.Label>
                                    <Form.Control
                                        type='number'
                                        name='globalSelectedPeriod'
                                        id='globalSelectedPeriod'
                                        value={globalSelectedPeriod?.year}
                                        readOnly
                                    />
                                </Form.Group>

                                {/* Organismos o Servicios según perfil */}
                                {profileName === 'Consulta por Organismo' ? (
                                    <OrganismTreeSelect
                                        treeData={filteredJurisdictionData}
                                        value={selectedOrganisms}
                                        setSelectedOrganisms={setSelectedOrganisms}
                                        loading={jurisdictionsLoading}
                                        variant="report"
                                        placeholder="Seleccione organismos"
                                    />
                                ) : (
                                    <Form.Group>
                                        <Form.Label className='text-black-color'>
                                            Servicio
                                        </Form.Label>
                                        <MultiselectComponent
                                            options={options}
                                            selected={selected}
                                            setSelected={setSelected}
                                            isLoading={administrativeServiceIsFetching}
                                        />
                                    </Form.Group>
                                )}

                                {/* Fecha */}
                                <FormProvider {...hookFormMethods}>
                                    <MaxDate {...{ dateTo, setDateTo, setDateHasErrors }} />
                                </FormProvider>

                                {/* Checkbox Mostrar solo los Expedientes con las etapas del gasto no igualadas */}
                                <Form.Group>
                                    <Form.Check
                                        type="checkbox"
                                        id="totalCheckAllow"
                                        label={checkWithoutBalanceLabel}
                                        className="text-black-color"
                                        checked={checkWithoutBalance}
                                        onChange={() => {
                                            setCheckWithoutBalance(!checkWithoutBalance);
                                        }}
                                    />
                                </Form.Group>

                                {/* Mensaje de reporte en proceso */}
                                {showMsgReportInProcess && (
                                    <Alert key={'1'} variant={'primary'} className='mt-4'>
                                        <b className='text-black-color'>Aviso:</b> El reporte se esta generando en segundo plano y estará disponible en el módulo de
                                        <a
                                            className='font-italic text-info cursor-pointer'
                                            onClick={() => dispatch(push(ROUTES.REPORT_BUDGET_STATUS_CREDIT_EXECUTION_GENERATED))}
                                        >
                                            &nbsp;reportes generados.
                                        </a>
                                    </Alert>
                                )}

                                {/* Botones */}
                                <Form.Group className='d-flex justify-content-between mt-4'>
                                    <Button size='lg' onClick={onClickPDF} disabled={!isFormValid()}>
                                        <FontAwesomeIcon icon={faFilePdf} className='mr-2' />
                                        Generar PDF
                                    </Button>
                                    <AppLoading isLoading={reportIsFetching} />
                                    <Button size='lg' onClick={onClickXLS} disabled={!isFormValid()}>
                                        <FontAwesomeIcon icon={faFileExcel} className='mr-2' />
                                        Generar XLS
                                    </Button>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default ReportBalanceStatusPage;